import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  Tooltip,
  Fade,
  Dialog,
  DialogContent,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import {
  Download,
  FolderInput,
  Trash2,
  SquareX,
  LayoutGrid,
  ArrowDownUp,
  List,
  Compass,
  SquareCheck,
  X,
} from 'lucide-mui';
import { useTheme } from '@mui/material/styles';
import type { ActionType, FileType } from '../../../types';
import type { RootState } from '../../../reducers';
import { deselectAllFiles, downloadMultipleFiles } from '../librarySlice';
import { RouteParams } from '../types';
import { useQuery } from '../../../hooks/urlHooks';
import SortComponent from './Sort';
import { LibraryViewType } from '../../../types';
import MediaActions from './mediaActions';
import MultiAskAI from '../files/MultiAskAI';
import { useUserTags } from '../../../hooks/useTags';
import { pushModal } from '../../globalModals/globalModalsSlice';
import TourCarousel from '../files/tour';
import { useSelectMode } from '../context/SelectModeContext';
import { setAlertMessage } from '../../dashboard/dashboardSlice';

type SelectedFileActionKeyType = 'download' | 'move' | 'delete' | 'deselect';
type ActionKeyType =
  | LibraryViewType
  | SelectedFileActionKeyType
  | 'sort'
  | 'features'
  | 'select';

type LibraryActionType = ActionType<ActionKeyType> & {
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  href?: string;
  id?: string;
};

export const LibraryActions = () => {
  const { t } = useTranslation();
  const { library, upload, import: importState } = useSelector(
    (state: RootState) => state
  );
  const { teamId, folderId } = useParams<RouteParams>();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { selectedFiles, files } = library;
  const showSelectedFileActions = Object.keys(selectedFiles).length > 0;
  const fileArray: FileType[] = Object.values(selectedFiles);
  const { getTag, setTag } = useUserTags();
  const { selectMode, setSelectMode } = useSelectMode();

  const viewFromQuery = useQuery().view as LibraryViewType | undefined;
  const savedView = getTag('library-view-type') as LibraryViewType | null;
  const [view, setView] = useState<LibraryViewType>(
    viewFromQuery || savedView || 'grid'
  );

  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [featuresModalOpen, setFeaturesModalOpen] = useState(false);

  React.useEffect(() => {
    if (showSelectedFileActions && !selectMode) {
      setSelectMode(true);
    }
  }, [showSelectedFileActions, selectMode, setSelectMode]);

  if (
    !teamId ||
    !folderId ||
    (files.data.length === 0 &&
      !upload.activeUploads.length &&
      importState.status !== 'pending')
  ) {
    return (
      <Box
        sx={{
          height: 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'stretch',
        }}
      >
        <Box>
          <MediaActions />
        </Box>
        <Box sx={{ height: 56 }} />
      </Box>
    );
  }

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleFeaturesClick = () => {
    setFeaturesModalOpen(true);
  };

  const handleClick = (
    actionKey: ActionKeyType,
    event?: React.MouseEvent<HTMLElement>
  ) => {
    switch (actionKey) {
      case 'list':
      case 'grid':
        setView(actionKey);
        setTag({ key: 'library-view-type', value: actionKey });
        break;
      case 'select': {
        const newSelectMode = !selectMode;
        setSelectMode(newSelectMode);
        if (!newSelectMode) {
          dispatch(deselectAllFiles());
        }
        break;
      }
      case 'download': {
        const fileCount = fileArray.length;
        dispatch(
          setAlertMessage({
            type: 'info',
            message: `Starting download of ${fileCount} file${
              fileCount > 1 ? 's' : ''
            }...`,
          })
        );

        dispatch(downloadMultipleFiles(fileArray));
        break;
      }
      case 'move':
        dispatch(pushModal({ type: 'move-file-modal', files: fileArray }));
        break;
      case 'delete':
        dispatch(pushModal({ type: 'delete-files-modal', files: fileArray }));
        break;
      case 'deselect':
        dispatch(deselectAllFiles());
        setSelectMode(false);
        break;
      case 'sort':
        handleSortClick(event!);
        break;
      case 'features':
        handleFeaturesClick();
        break;
      default:
        break;
    }
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const selectedFileActions: LibraryActionType[] = [
    {
      key: 'download',
      label: 'Download',
      startIcon: <Download />,
    },
    {
      key: 'move',
      label: 'Move',
      startIcon: <FolderInput />,
    },
    {
      key: 'delete',
      label: 'Delete',
      startIcon: <Trash2 />,
    },
    {
      key: 'deselect',
      label: 'Deselect',
      startIcon: <SquareX />,
    },
  ];

  const leftActions: LibraryActionType[] =
    files.data.length <= 3
      ? [
          {
            key: 'features',
            label: 'Tour Features',
            id: 'features-button',
            startIcon: <Compass />,
            onClick: handleFeaturesClick,
          },
        ]
      : [];

  const fixedActions: LibraryActionType[] = [
    {
      key: 'select',
      label: selectMode
        ? t('library.actions.exitSelectMode', 'Exit Select Mode')
        : t('library.actions.selectFiles', 'Select Files'),
      id: 'select-button',
      startIcon: selectMode ? <SquareX /> : <SquareCheck />,
      onClick: () => handleClick('select'),
    },
    {
      key: 'sort',
      label: t('library.actions.sort', 'Sort'),
      id: 'sort-button',
      startIcon: <ArrowDownUp />,
      onClick: handleSortClick,
    },
    {
      key: view === 'grid' ? 'list' : 'grid',
      label:
        view === 'grid'
          ? t('library.actions.listView', 'List View')
          : t('library.actions.gridView', 'Grid View'),
      id: 'list-view-button',
      onClick: () => handleClick(view === 'grid' ? 'list' : 'grid'),
      startIcon: view === 'list' ? <LayoutGrid /> : <List />,
    },
  ];

  const renderActionButton = (action: LibraryActionType) => (
    <Box key={action.key}>
      {action.key === 'features' ? (
        <IconButton
          aria-label={action.label}
          id={action.id}
          size="medium"
          sx={{
            borderRadius: 6,
            minWidth: 'auto',
            minHeight: 32,
            px: 2,
            py: 0.5,
            backgroundColor: theme.palette.background.paper,
            fontSize: '0.75rem',
            fontWeight: 500,
            color: 'text.secondary',
            textTransform: 'none',
            boxShadow: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
          onClick={(event: React.MouseEvent<HTMLElement>) =>
            action.onClick
              ? action.onClick(event)
              : handleClick(action.key, event)
          }
        >
          {React.cloneElement(action.startIcon as React.ReactElement, {
            size: 14,
          })}
          {action.label}
        </IconButton>
      ) : (
        <Tooltip title={action.label} arrow>
          <IconButton
            aria-label={action.label}
            id={action.id}
            size="medium"
            href={action.href}
            component={action.href ? 'a' : 'button'}
            sx={{
              borderRadius: '50%',
              minWidth: 40,
              minHeight: 40,
              backgroundColor:
                view === action.key || (action.key === 'select' && selectMode)
                  ? theme.palette.secondary.main
                  : 'transparent',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                transform: 'scale(1.05)',
              },
            }}
            onClick={(event: React.MouseEvent<HTMLElement>) =>
              action.onClick
                ? action.onClick(event)
                : handleClick(action.key, event)
            }
          >
            {React.cloneElement(action.startIcon as React.ReactElement, {
              size: 'large',
            })}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        paddingBottom: 0,
      }}
      data-select-mode={selectMode}
    >
      <Box>
        <MediaActions />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'row',
          alignItems: 'center',
          paddingBottom: 0,
        }}
      >
        <Box sx={{ ml: 2, flex: 1, display: 'flex', alignItems: 'center' }}>
          {/* Left side actions */}
          <Box sx={{ display: 'flex', gap: 0, mr: 2 }}>
            {leftActions.map(renderActionButton)}
          </Box>
          {/* <Searchbar /> */}
        </Box>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            minHeight: 48,
          }}
        >
          <Fade in={showSelectedFileActions} timeout={200}>
            <Box
              sx={{
                position: showSelectedFileActions ? 'static' : 'absolute',
                display: showSelectedFileActions ? 'flex' : 'none',
                justifyContent: 'flex-end',
                gap: 0,
                mr: 2,
                alignItems: 'center',
                mb: 0,
              }}
            >
              {selectedFileActions.map(({ key, startIcon, label }) => (
                <Box key={key}>
                  <Tooltip title={label}>
                    <IconButton
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        handleClick(key, event)
                      }
                      aria-label={label}
                      size="medium"
                      sx={{
                        borderRadius: '50%',
                        minWidth: 40,
                        minHeight: 40,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      {React.cloneElement(startIcon as React.ReactElement, {
                        size: 'large',
                      })}
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          </Fade>

          <Fade in={!showSelectedFileActions} timeout={200}>
            <Box
              sx={{
                position: !showSelectedFileActions ? 'static' : 'absolute',
                display: !showSelectedFileActions ? 'flex' : 'none',
                justifyContent: 'flex-end',
                gap: 0,
                mr: 2,
                alignItems: 'center',
                mb: 0,
              }}
            >
              {fixedActions.map(renderActionButton)}
              <SortComponent
                anchorEl={sortAnchorEl}
                onClose={handleSortClose}
              />
            </Box>
          </Fade>
        </Box>
      </Box>

      {/* Features Tour Modal */}
      <Dialog
        open={featuresModalOpen}
        onClose={() => setFeaturesModalOpen(false)}
        maxWidth={false}
        sx={{
          '& .MuiDialog-paper': {
            width: '100vw',
            height: '100vh',
            maxWidth: 'none',
            maxHeight: 'none',
            m: 0,
            borderRadius: 0,
          },
        }}
      >
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100vh',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: theme.palette.background.default,
              '& > div': {
                width: '100vw !important', // Override TourCarousel width constraints
              },
            }}
          >
            <IconButton
              onClick={() => setFeaturesModalOpen(false)}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 1000,
                backgroundColor: theme.palette.background.paper,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <X />
            </IconButton>
            <TourCarousel />
          </Box>
        </DialogContent>
      </Dialog>

      {templateModalOpen && (
        <MultiAskAI
          open={templateModalOpen}
          onClose={() => setTemplateModalOpen(false)}
        />
      )}
    </Box>
  );
};

export default LibraryActions;
